import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useBasePath } from '../../../../lib/use-base-path';
import { queryKeys } from '../../../../lib/query-client';
import { profileLinksEnabled } from '../../../../lib/profile-links-flag';
import { useProfileLinksList } from '../../../../lib/use-profile-links';
import {
  deriveProfileLinkCardState,
  profileLinkCardDisplayUrl,
  profileLinkEditorRoute,
  profileLinkListRefreshKey,
  type ProfileLinkCardData,
} from '../../../../lib/profile-link-chat-card';
import { Badge, Button, Card, Toast, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './ProfileLinkChatCard.module.css';

// "Your Symphony card" under an assistant reply carrying `metadata.profileLinkCard` — the agent created, claimed or
// changed the owner's profile link, and it is already live (there is no publish step a user sees). Same states, copy
// and BI on iOS (`ProfileLinkChatCard`) and Android; all the rules live in lib/profile-link-chat-card.ts.
//
// `card` is the LATEST card for this link in the conversation (MessagesPanel's `latestProfileLinkCards`), so an
// earlier card follows a later pause or archive instead of saying Live with a dead Open.

// The disagreements already re-read in this page — module-wide, so the several cards of one link (and every
// re-render) trigger ONE re-read per disagreement, never a loop.
const refreshedDisagreements = new Set<string>();

/** Test seam: forget the re-reads, so each test starts from a fresh page. */
export function resetProfileLinkCardRefreshes(): void {
  refreshedDisagreements.clear();
}

export function ProfileLinkChatCard({
  card,
  messageId,
  readOnly = false,
}: {
  card: ProfileLinkCardData;
  /** The row's persisted id (finalId when streamed) — rides the BI clicks. */
  messageId?: string;
  /** A thread that accepts nothing from here (a channel-locked thread): the card shows its state, no owner actions. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>({ open: false, message: '', severity: 'error' });

  // The live state: the owner's own list, when the feature is on for them (the gate fails closed, and a closed gate
  // simply leaves the card on its minted status).
  const gate = useQuery({ queryKey: queryKeys.profileLinksGate, queryFn: profileLinksEnabled, staleTime: Infinity });
  const list = useProfileLinksList(gate.data === true);
  const live = list.data?.links.find((link) => link.id === card.linkId) ?? null;
  const state = deriveProfileLinkCardState({ card, live });

  // The list was read before the agent's write: read it again, once per disagreement.
  const refreshKey = profileLinkListRefreshKey(card, list.data?.links);
  const refetchList = list.refetch;
  useEffect(() => {
    if (!refreshKey || refreshedDisagreements.has(refreshKey)) return;
    refreshedDisagreements.add(refreshKey);
    void refetchList();
  }, [refreshKey, refetchList]);

  // Clicks only, like iOS and Android: no impression event on any surface.
  const biFields = { linkId: card.linkId, messageId, state };
  const click = (name: 'edit' | 'open' | 'share') =>
    logPageClickBi({ ...CHAT_HOME_BI, button_name: `profile-link-card-${name}` }, { additionalFields: biFields });

  if (state === 'gone') return null;

  const onEdit = () => {
    click('edit');
    const route = profileLinkEditorRoute(card.linkId);
    void router.push(route, `${basePath}${route}`);
  };

  const onOpen = () => {
    click('open');
    window.open(card.url, '_blank', 'noopener,noreferrer');
  };

  const onShare = async () => {
    click('share');
    // The system share sheet where the browser has one (phones); everywhere else, copy the link.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: card.url });
        return;
      } catch (err) {
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(card.url);
      setToast({ open: true, message: t('common.copied'), severity: 'success' });
    } catch {
      // clipboard refused (permissions / insecure context) — Open still hands the address out
    }
  };

  const title = card.contextTitle ? `${t('chat.profileLinkCard.title')} · ${card.contextTitle}` : t('chat.profileLinkCard.title');
  const badge =
    state === 'live'
      ? { label: t('chat.profileLinkCard.live'), variant: 'active' as const }
      : state === 'paused'
      ? { label: t('chat.profileLinkCard.paused'), variant: 'paused' as const }
      : { label: t('chat.profileLinkCard.draft'), variant: 'waiting-on-you' as const };

  return (
    <div>
      <Card className={styles.card} data-testid="profile-link-chat-card" data-state={state}>
        <div className={styles.header}>
          <Typography variant="h4" component="div" color="strong">
            {title}
          </Typography>
          <Badge role="status" variant={badge.variant} label={badge.label} />
        </div>
        <div className={styles.text}>
          <Typography variant="p3" color="secondary" className={styles.address} dir="ltr">
            {profileLinkCardDisplayUrl(card.url)}
          </Typography>
          {(live?.card.headline || card.headline) && (
            <Typography variant="p3" color="strong" dir="auto">
              {live?.card.headline || card.headline}
            </Typography>
          )}
        </div>
        <div className={styles.actions}>
          {state === 'live' && (
            <>
              <Button size="small" onClick={onOpen}>
                {t('chat.profileLinkCard.open')}
              </Button>
              <Button size="small" variant="outlined" onClick={() => void onShare()}>
                {t('chat.profileLinkCard.share')}
              </Button>
            </>
          )}
          {(state === 'draft' || state === 'paused') && !readOnly && (
            <Button size="small" variant="outlined" onClick={onEdit}>
              {t('chat.profileLinkCard.edit')}
            </Button>
          )}
        </div>
      </Card>
      <Toast open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast((c) => ({ ...c, open: false }))} />
    </div>
  );
}
